import { debounce, isMobileBreakpoint, getLargestHeight, checkBrowserType } from "../utility/UtilityFunctions";
import { scrollDirection } from "../utility/ScrollDirection";
import { optimisedScrollAnimation } from "../utility/OptimisedScroll";

/**
 * Used to setup site critical js such as mobile/desktop switcher, optimised scroll event configurations etc...
 *
 * This should be run before all other theme related js
 */
export default class SiteVitals {
    constructor() {
        this.elements = {
            root: document.documentElement,
            html: document.documentElement,
            body: document.body,
            header: document.querySelector('.c-page-header'),
            footer: document.querySelector('.c-page-footer'),
            primaryNav: document.querySelector('.c-nav-primary'),
            announcementBanner: document.querySelector('.c-announcement-header-banner'),
        };

        this.classes = {
            isActive: 'is-active',
            navActive: 'navigation-is-active',
        };

        // Register Custom Scroll Event
        optimisedScrollAnimation("scroll", "optimisedScroll");

        // Check browser and apply class
        checkBrowserType();

        this.setAnnouncementBannerHeight();
        this.initIntersectionObservers();
        this.bindEventListeners();
    }

    setAnnouncementBannerHeight() {
        if (this.elements.announcementBanner) {
            const slides = this.elements.announcementBanner.querySelectorAll('.swiper-slide');
            const bannerHeight = slides.length
                ? getLargestHeight(slides) / 16 + 2
                : this.elements.announcementBanner.querySelector('p')?.getBoundingClientRect().height / 16 + 2;

            this.elements.root.style.setProperty('--announcement-banner-height', `${bannerHeight}rem`);
        } else {
            this.elements.root.style.setProperty('--announcement-banner-height', `0rem`);
        }
    }

    initIntersectionObservers() {
        // Detect when footer is in view
        if (this.elements.footer) {
            this.footerObserver = new IntersectionObserver(([entry]) => {
                this.elements.body.classList.toggle("footer-in-view", entry.isIntersecting);
            }, { threshold: 0.01 });
            this.footerObserver.observe(this.elements.footer);
        }

        // Detect autoplay videos
        this.videoObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                entry.target[entry.isIntersecting ? 'play' : 'pause']();
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('video[autoplay]').forEach(video => this.videoObserver.observe(video));
    }

    bindEventListeners() {
        window.addEventListener('optimisedScroll', () => {
            window.scrollDirection = scrollDirection();

            if (this.elements.html.classList.contains(this.classes.navActive)) {
                return this.elements.html.setAttribute('data-scroll-direction', `up`);
            }

            this.elements.html.setAttribute('data-scroll-direction', `${window?.scrollDirection?.direction}`);
            this.pageIsScrolled();
        });

        window.addEventListener('resize', debounce(() => {
            isMobileBreakpoint();
            this.setAnnouncementBannerHeight();
        }, 250));

        // when scrolling finishes, wait, then reset scroll direction - used to reshow headers
        let timeoutId;
        window.addEventListener('optimisedScroll', debounce(() => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                this.elements.html.setAttribute('data-scroll-direction', 'up');
            }, 700);
        }, 500));
    }

    pageIsScrolled() {
        requestAnimationFrame(() => {
            let threshold = window.innerWidth >= 768 ? 30 : 0;

            if (this.elements.html.classList.contains('animated-hero-detected')) {
                threshold = document.querySelector('.so-widget-journey-animated-hero').clientHeight - this.elements.header.clientHeight
            }

            this.elements.body.classList.toggle("page-is-scrolled", window.pageYOffset > threshold);
        });
    }
}
